
import { mapActions, mapState } from "vuex";
import UploadFile from "@/components/UploadFile.vue";
import VueRecaptcha from "vue-recaptcha";
import { VUE_APP_RECAPTCHA_KEY } from "@/commons/config";
import SaneamentoService from "@/services/gessan/saneamento.service";
import AuthService from "@/services/gessan/auth.service";
import { GessanApiEnvelope, TipoDocumentoGessan } from "@/models/gessan/tipoDocumento";
import OpenDialogConfirmar from "@/components/OpenDialogConfirmar.vue";
import DialoError from "@/components/DialoError.vue";
import { router } from "@/router";
import FunctionsCommuns from "@/util/FunctionsCommuns";
import MonthFilterPicker from "@/views/gessan/Saneamento/components/MonthFilterPicker.vue";

type FieldType = "integer" | "currency";

interface FieldDefinition {
  model: string;
  documentKey: string;
  label: string;
  documentLabel: string;
  type: FieldType;
  backendDescription: string;
}

const municipioFields: FieldDefinition[] = [
  {
    model: "qtdeUnAbastecimentoAgua",
    documentKey: "qtdeUnAbastecimentoAguaDocumento",
    label: "Quantidade de Ligações Ativas de Água *",
    documentLabel: "Comprovante de Quantidade de Ligações Ativas de Água *",
    type: "integer",
    backendDescription: "Quantidade de ligações ativas de água",
  },
  {
    model: "qtdeUnEsgotamentoSanitario",
    documentKey: "qtdeUnEsgotamentoSanitarioDocumento",
    label: "Quantidade de Ligações Ativas de Esgoto *",
    documentLabel: "Comprovante de Quantidade de Ligações Ativas de Esgoto *",
    type: "integer",
    backendDescription: "Quantidade de ligações ativas de esgoto",
  },
  {
    model: "faturamentoMensalLiquido",
    documentKey: "faturamentoMensalLiquidoDocumento",
    label: "Faturamento Direto Mensal Líquido *",
    documentLabel: "Comprovante de Faturamento Direto Mensal Líquido *",
    type: "currency",
    backendDescription: "Faturamento direto mensal líquido",
  },
];

const grupoMunicipioFields: FieldDefinition[] = [
  {
    model: "quantidadeLigacoesAtivasAguaCapital",
    documentKey: "quantidadeLigacoesAtivasAguaCapitalDocumento",
    label: "Quantidade de Ligações Ativas de Água Capital *",
    documentLabel:
      "Comprovante de Quantidade de Ligações Ativas de Água Capital *",
    type: "integer",
    backendDescription: "Quantidade de ligações ativas de água - Capital",
  },
  {
    model: "quantidadeLigacoesAtivasAguaInterior",
    documentKey: "quantidadeLigacoesAtivasAguaInteriorDocumento",
    label: "Quantidade de Ligações Ativas de Água Interior *",
    documentLabel:
      "Comprovante de Quantidade de Ligações Ativas de Água Interior *",
    type: "integer",
    backendDescription: "Quantidade de ligações ativas de água - Interior",
  },
  {
    model: "quantidadeLigacoesAtivasEsgotoCapital",
    documentKey: "quantidadeLigacoesAtivasEsgotoCapitalDocumento",
    label: "Quantidade de Ligações Ativas de Esgoto Capital *",
    documentLabel:
      "Comprovante de Quantidade de Ligações Ativas de Esgoto Capital *",
    type: "integer",
    backendDescription: "Quantidade de ligações ativas de esgoto - Capital",
  },
  {
    model: "quantidadeLigacoesAtivasEsgotoInterior",
    documentKey: "quantidadeLigacoesAtivasEsgotoInteriorDocumento",
    label: "Quantidade de Ligações Ativas de Esgoto Interior *",
    documentLabel:
      "Comprovante de Quantidade de Ligações Ativas de Esgoto Interior *",
    type: "integer",
    backendDescription: "Quantidade de ligações ativas de esgoto - Interior",
  },
  {
    model: "faturamentoLiquidoAguaCapital",
    documentKey: "faturamentoLiquidoAguaCapitalDocumento",
    label: "Faturamento Líquido de Água Capital *",
    documentLabel: "Comprovante de Faturamento Líquido de Água Capital *",
    type: "currency",
    backendDescription: "Faturamento líquido de água - Capital",
  },
  {
    model: "faturamentoLiquidoAguaInterior",
    documentKey: "faturamentoLiquidoAguaInteriorDocumento",
    label: "Faturamento Líquido de Água Interior *",
    documentLabel: "Comprovante de Faturamento Líquido de Água Interior *",
    type: "currency",
    backendDescription: "Faturamento líquido de água - Interior",
  },
  {
    model: "faturamentoLiquidoEsgotoCapital",
    documentKey: "faturamentoLiquidoEsgotoCapitalDocumento",
    label: "Faturamento Líquido de Esgoto Capital *",
    documentLabel: "Comprovante de Faturamento Líquido de Esgoto Capital *",
    type: "currency",
    backendDescription: "Faturamento líquido de esgoto - Capital",
  },
  {
    model: "faturamentoLiquidoEsgotoInterior",
    documentKey: "faturamentoLiquidoEsgotoInteriorDocumento",
    label: "Faturamento Líquido de Esgoto Interior *",
    documentLabel: "Comprovante de Faturamento Líquido de Esgoto Interior *",
    type: "currency",
    backendDescription: "Faturamento líquido de esgoto - Interior",
  },
];

const fieldDefinitionsByDocumentKey = [...municipioFields, ...grupoMunicipioFields].reduce(
  (acc, field) => {
    acc[field.documentKey] = field;
    return acc;
  },
  {} as Record<string, FieldDefinition>
);

const defaultCreateInformeErrorMessage =
  "Só é permitido cadastrar competências até o mês anterior e sem informe existente com situação diferente de CANCELADO ou NEGADO.";

const defaultCreateInformeErrorMessageMunicipio =
  "Não é possível realizar informe após o dia 15 até o final do mês!";

const noAvailableCompetenciaMessage =
  "Não há competências disponíveis para cadastro até o mês anterior. Verifique a listagem das remunerações.";

const listagemCompetenciasLoadErrorMessage =
  "Não foi possível carregar a listagem das remunerações para validar as competências disponíveis.";

const actions = mapActions("gessan", ["setForm"]);
const state = mapState("gessan", ["form"]);
const global = mapState("global", ["userGessan"]);

export default {
  name: "Saneamento",
  components: {
    DialoError,
    MonthFilterPicker,
    OpenDialogConfirmar,
    VueRecaptcha,
    UploadFile,
  },
  data: () => ({
    siteKey: VUE_APP_RECAPTCHA_KEY,
    filesErros: [],
    filesTypeErros: [],
    rules: {
      required: value => !!value || "Esse campo deve ser preenchido!",
      onlyNumbers: value => {
        if (value === "") {
          return true;
        }

        const num = value ? value.toString().replaceAll(".", "") : "";
        return /^[0-9]*[.]?[0-9]*$/.test(num) || "Esse campo só permite números";
      },
    },
    isLoading: false,
    openDialogConfirmarInforme: false,
    openDialogErro: false,
    isCreateDisabled: false,
    mensagemErro: defaultCreateInformeErrorMessage,
    focusText: false,
    tiposDocumento: [] as TipoDocumentoGessan[],
    tiposDocumentoCarregados: false,
    documentosConfigInvalid: false,
    competenciasCarregadas: false,
    competenciasBloqueadas: [] as string[],
    falhaAoCarregarCompetencias: false,
  }),
  computed: {
    ...state,
    ...global,
    isGrupoMunicipio() {
      return !!this.userGessan?.isGrupo;
    },
    formTitle() {
      return this.isGrupoMunicipio
        ? "Informe de Repasse de Regulação - Grupo Município"
        : "Informe de Repasse de Regulação";
    },
    baseFields() {
      return this.isGrupoMunicipio ? grupoMunicipioFields : municipioFields;
    },
    maxCompetenciaPermitida() {
      return this.getMaxCompetenciaPermitida();
    },
    isFormDisabled() {
      return this.isCreateDisabled
        || !this.tiposDocumentoCarregados
        || this.documentosConfigInvalid
        || (this.isGrupoMunicipio && !this.competenciasCarregadas)
        || (this.isGrupoMunicipio && this.falhaAoCarregarCompetencias);
    },
    activeFields() {
      const configuredDocuments = this.getConfiguredDocuments();

      if (!configuredDocuments.length) {
        return [];
      }

      const resolvedFields = configuredDocuments
        .map(documento => {
          const field = this.resolveFieldDefinition(documento);

          if (!field) {
            return null;
          }

          return {
            ...field,
            documentLabel: documento?.descricao || field.documentLabel,
          };
        })
        .filter(Boolean);

      return this.applyFieldRules(resolvedFields);
    },
    hasDocumentConfigurationMismatch() {
      const configuredDocuments = this.getConfiguredDocuments();

      if (!configuredDocuments.length) {
        return false;
      }

      return this.activeFields.length !== configuredDocuments.length;
    },
  },
  watch: {
    focusText: {
      handler() {
        if (!this.focusText) {
          this.formatFields();
        }
      },
      deep: true,
    },
  },
  mounted() {
    this.focusText = true;
    this.loadTiposDocumento();
    if (this.isGrupoMunicipio) {
      this.loadCompetenciasDisponiveis();
      return;
    }

    this.mes();
    this.checkIfCanCreateInforme();
  },
  destroyed() {
    this.cleanForm();
  },
  methods: {
    ...actions,
    applyFieldRules(fields: FieldDefinition[]) {
      return fields.map(field => ({
        ...field,
        rules:
          field.type === "integer"
            ? [this.rules.required, this.rules.onlyNumbers]
            : [this.rules.required],
      }));
    },
    sanitizeIntegerInput(value) {
      const normalizedValue = value ? value.toString().replace(/\D/g, "") : "";

      return normalizedValue
        ? FunctionsCommuns.IntegerFormatTextField(normalizedValue)
        : "";
    },
    sanitizeCurrencyInput(value) {
      const digitsOnly = value ? value.toString().replace(/\D/g, "") : "";

      if (!digitsOnly) {
        return "";
      }

      return FunctionsCommuns.MonetaryFormat((Number(digitsOnly) / 100).toFixed(2));
    },
    sanitizeFieldValue(field: FieldDefinition, value) {
      const currentValue = value ?? this.form[field.model];

      this.form[field.model] =
        field.type === "currency"
          ? this.sanitizeCurrencyInput(currentValue)
          : this.sanitizeIntegerInput(currentValue);
    },
    onFieldInput(field: FieldDefinition, value) {
      this.sanitizeFieldValue(field, value);
    },
    onFieldPaste(field: FieldDefinition, event) {
      const pastedText = event.clipboardData?.getData("text") || "";
      this.sanitizeFieldValue(field, pastedText);
    },
    blockInvalidFieldKey(field: FieldDefinition, event) {
      const teclasPermitidas = [
        "Backspace",
        "Delete",
        "Tab",
        "Escape",
        "Enter",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Home",
        "End",
      ];

      if (teclasPermitidas.includes(event.key) || event.ctrlKey || event.metaKey) {
        return;
      }

      const isAllowedKey = /^\d$/.test(event.key);

      if (!isAllowedKey) {
        event.preventDefault();
      }
    },
    mes() {
      const hoje = new Date();
      const mesAnterior = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
      const ano = mesAnterior.getFullYear();
      const mes = String(mesAnterior.getMonth() + 1).padStart(2, '0');
      this.form.mesAno= `${mes}/${ano}`
    },
    validacaoMesAno() {
      if (this.isGrupoMunicipio) {
        return true;
      }

      const hoje = new Date();
      const dia = hoje.getDate();
      return dia >= 1 && dia <= 15;
    },
    onCloseDialogErro() {
      this.openDialogErro = false;
    },
    onChangeMesAno(value) {
      this.form.mesAno = value;
    },
    verifyRecaptcha(value) {
      this.form.recaptcha = value;
    },
    expiredRecaptcha() {
      this.$refs.recaptcha.reset();
      this.form.recaptcha = null;
    },
    onChangeFile(value, nome) {
      if ("application/pdf" === value.type) {
        this.form[nome] = value;
        this.filesErros = this.filesErros.filter(f => f !== nome);
        this.filesTypeErros = this.filesTypeErros.filter(f => f !== nome);
      } else {
        this.form[nome] = null;
        this.filesTypeErros = [...this.filesTypeErros, nome];
      }
    },
    normalizeText(value: string) {
      return value
        ?.normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .toLowerCase();
    },
    getRequiredDocumentLabel(label: string) {
      if (!label) {
        return "Documento *";
      }

      return label.includes("*") ? label : `${label} *`;
    },
    normalizeCompetenciaValue(value: string | null | undefined) {
      if (!value) {
        return null;
      }

      if (value.includes("/")) {
        const [month, year] = value.split("/");

        if (!month || !year) {
          return null;
        }

        return `${year}-${month.padStart(2, "0")}`;
      }

      if (/^\d{4}-\d{2}/.test(value)) {
        return value.slice(0, 7);
      }

      return null;
    },
    formatCompetencia(value: string | null | undefined) {
      const normalizedValue = this.normalizeCompetenciaValue(value);

      if (!normalizedValue) {
        return null;
      }

      const [year, month] = normalizedValue.split("-");
      return `${month}/${year}`;
    },
    getMaxCompetenciaPermitida() {
      const hoje = new Date();
      const mesAnterior = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
      const ano = mesAnterior.getFullYear();
      const mes = String(mesAnterior.getMonth() + 1).padStart(2, "0");

      return `${ano}-${mes}`;
    },
    isSituacaoCancelada(situacao: string | null | undefined) {
      return this.normalizeText(situacao || "") === "cancelado";
    },
    isSituacaoNegada(situacao: string | null | undefined) {
      return this.normalizeText(situacao || "") === "negado";
    },
    isSituacaoBloqueante(situacao: string | null | undefined) {
      return !this.isSituacaoCancelada(situacao) && !this.isSituacaoNegada(situacao);
    },
    isCompetenciaBloqueada(value: string | null | undefined) {
      const competencia = this.normalizeCompetenciaValue(value);

      if (!competencia) {
        return false;
      }

      return this.competenciasBloqueadas.includes(competencia);
    },
    isCompetenciaPermitida(value: string | null | undefined) {
      const competencia = this.normalizeCompetenciaValue(value);

      if (!competencia) {
        return false;
      }

      return (
        competencia <= this.maxCompetenciaPermitida
        && !this.isCompetenciaBloqueada(competencia)
      );
    },
    allowedCompetenciaDate(value: string) {
      return this.isCompetenciaPermitida(value);
    },
    getPreviousCompetencia(value: string) {
      const normalizedValue = this.normalizeCompetenciaValue(value);

      if (!normalizedValue) {
        return null;
      }

      const [year, month] = normalizedValue.split("-").map(Number);
      const previousMonth = new Date(year, month - 2, 1);

      return `${previousMonth.getFullYear()}-${String(previousMonth.getMonth() + 1).padStart(
        2,
        "0"
      )}`;
    },
    resolveCompetenciaInicial() {
      let competencia = this.maxCompetenciaPermitida;
      let guard = 0;

      while (competencia && guard < 600) {
        if (this.isCompetenciaPermitida(competencia)) {
          return this.formatCompetencia(competencia);
        }

        competencia = this.getPreviousCompetencia(competencia);
        guard += 1;
      }

      return null;
    },
    applyCompetenciaDisponivel() {
      if (this.falhaAoCarregarCompetencias) {
        this.form.mesAno = null;
        this.isCreateDisabled = true;
        this.mensagemErro = listagemCompetenciasLoadErrorMessage;
        this.openDialogErro = true;
        return;
      }

      if (this.isCompetenciaPermitida(this.form.mesAno)) {
        this.isCreateDisabled = false;
        this.openDialogErro = false;
        this.mensagemErro = defaultCreateInformeErrorMessage;
        return;
      }

      const competenciaInicial = this.resolveCompetenciaInicial();

      if (!competenciaInicial) {
        this.form.mesAno = null;
        this.isCreateDisabled = true;
        this.mensagemErro = noAvailableCompetenciaMessage;
        this.openDialogErro = true;
        return;
      }

      this.form.mesAno = competenciaInicial;
      this.isCreateDisabled = false;
      this.openDialogErro = false;
      this.mensagemErro = defaultCreateInformeErrorMessage;
    },
    loadCompetenciasDisponiveis() {
      if (!this.isGrupoMunicipio) {
        return;
      }

      this.competenciasCarregadas = false;
      this.falhaAoCarregarCompetencias = false;
      this.competenciasBloqueadas = [];

      SaneamentoService.getList(
        {
          mesAno: null,
          qtdeUnAbastecimentoAgua: null,
          qtdeUnEsgotamentoSanitario: null,
          faturamentoMensalLiquido: null,
          municipio: null,
          situacao: null,
          situacaoDae: null,
          page: 0,
          size: 1000,
          totalElements: null,
          cnpj: this.userGessan.cnpj,
          sortBy: "mesAno",
          sortOrder: "DESC",
        },
        this.isGrupoMunicipio
      ).subscribe({
        next: response => {
          const result = (response as any)?.result;
          const competenciasBloqueadas = (result?.data || [])
            .filter(item => item?.mesAno && this.isSituacaoBloqueante(item?.situacao))
            .map(item => this.normalizeCompetenciaValue(item.mesAno))
            .filter(Boolean);

          this.competenciasBloqueadas = [...new Set(competenciasBloqueadas)];
          this.competenciasCarregadas = true;
          this.applyCompetenciaDisponivel();
        },
        error: () => {
          this.competenciasCarregadas = true;
          this.falhaAoCarregarCompetencias = true;
          this.applyCompetenciaDisponivel();
        },
      });
    },
    getCompetenciaValidationMessage() {
      const competencia = this.normalizeCompetenciaValue(this.form.mesAno);

      if (!competencia) {
        return "Selecione uma competência válida.";
      }

      if (competencia > this.maxCompetenciaPermitida) {
        return "Só é permitido cadastrar competências até o mês anterior.";
      }

      return "Já existe informe cadastrado para essa competência com situação diferente de CANCELADO ou NEGADO.";
    },
    normalizeArrayResponse<T>(response: GessanApiEnvelope<T[]> | T[] | undefined | null): T[] {
      const payload = (response as GessanApiEnvelope<T[]>)?.result
        ?? (response as GessanApiEnvelope<T[]>)?.data
        ?? response;

      return Array.isArray(payload) ? payload.filter(Boolean) : [];
    },
    extractResponseData(response) {
      if (response && typeof response === "object" && "data" in response) {
        return response.data;
      }

      return response;
    },
    normalizePodeCriarInformeResponse(
      response: boolean | GessanApiEnvelope<boolean> | undefined | null
    ): boolean {
      if (typeof response === "boolean") {
        return response;
      }

      // `result === true` é confiável no backend; `false` pode ser falso negativo (ex.: regra de dia).
      return response?.result === true;
    },
    getConfiguredDocuments(): TipoDocumentoGessan[] {
      return this.tiposDocumento.filter(Boolean);
    },
    resolveFieldDefinition(documento: TipoDocumentoGessan) {
      const explicitDocumentKey =
        documento?.nome || documento?.codigo || documento?.tipoDocumento;

      if (explicitDocumentKey && fieldDefinitionsByDocumentKey[explicitDocumentKey]) {
        return fieldDefinitionsByDocumentKey[explicitDocumentKey];
      }

      const descricao = this.normalizeText(documento?.descricao || "");

      return this.baseFields.find(
        field => this.normalizeText(field.backendDescription) === descricao
      );
    },
    loadTiposDocumento() {
      this.tiposDocumentoCarregados = false;
      this.documentosConfigInvalid = false;

      SaneamentoService.getTiposDocumento(
        this.userGessan.cnpj,
        this.isGrupoMunicipio
      ).subscribe({
        next: response => {
          const tiposDocumento = this.normalizeArrayResponse(
            this.extractResponseData(response)
          ) as TipoDocumentoGessan[];

          this.tiposDocumento = tiposDocumento;
          this.tiposDocumentoCarregados = true;
          this.documentosConfigInvalid =
            !tiposDocumento.length || this.hasDocumentConfigurationMismatch;

          if (this.documentosConfigInvalid) {
            AuthService.globalStore(
              "warning",
              "A configuração de documentos retornada pelo backend está incompleta ou incompatível com o cadastro."
            );
          }
        },
        error: () => {
          this.tiposDocumento = [];
          this.tiposDocumentoCarregados = false;
          this.documentosConfigInvalid = true;
          AuthService.globalStore("warning", "Não foi possível carregar a configuração de documentos.");
        },
      });
    },
    validateDocumentos() {
      this.filesErros = [];

      this.activeFields.forEach(field => {
        if (!this.form[field.documentKey]) {
          this.filesErros = [...this.filesErros, field.documentKey];
        }
      });

      const hasInvalidDocumentType = this.activeFields.some(field =>
        this.filesTypeErros.includes(field.documentKey)
      );

      return !this.filesErros.length && !hasInvalidDocumentType;
    },
    onClose(){
      this.openDialogConfirmarInforme=false
    },
    normalizeInteger(value) {
      return value ? value.toString().replaceAll(".", "") : "";
    },
    normalizeCurrency(value) {
      return value
        ? value
            .toString()
            .replaceAll(".", "")
            .replaceAll(",", ".")
            .replaceAll("R$", "")
            .trim()
        : "";
    },
    parseIntegerValue(value) {
      const normalizedValue = this.normalizeInteger(value);
      return normalizedValue ? parseInt(normalizedValue, 10) : 0;
    },
    parseCurrencyValue(value) {
      const normalizedValue = this.normalizeCurrency(value);
      return normalizedValue ? parseFloat(normalizedValue) : 0;
    },
    buildPayload() {
      if (!this.isGrupoMunicipio) {
        return {
          cnpj: this.userGessan.cnpj,
          mesAno: this.form.mesAno,
          qtdeUnAbastecimentoAgua: this.normalizeInteger(this.form.qtdeUnAbastecimentoAgua),
          qtdeUnEsgotamentoSanitario: this.normalizeInteger(
            this.form.qtdeUnEsgotamentoSanitario
          ),
          faturamentoMensalLiquido: parseFloat(
            this.normalizeCurrency(this.form.faturamentoMensalLiquido)
          ),
        };
      }

      const quantidadeLigacoesAtivasAguaCapital = this.parseIntegerValue(
        this.form.quantidadeLigacoesAtivasAguaCapital
      );
      const quantidadeLigacoesAtivasAguaInterior = this.parseIntegerValue(
        this.form.quantidadeLigacoesAtivasAguaInterior
      );
      const quantidadeLigacoesAtivasEsgotoCapital = this.parseIntegerValue(
        this.form.quantidadeLigacoesAtivasEsgotoCapital
      );
      const quantidadeLigacoesAtivasEsgotoInterior = this.parseIntegerValue(
        this.form.quantidadeLigacoesAtivasEsgotoInterior
      );
      const faturamentoLiquidoAguaCapital = this.parseCurrencyValue(
        this.form.faturamentoLiquidoAguaCapital
      );
      const faturamentoLiquidoAguaInterior = this.parseCurrencyValue(
        this.form.faturamentoLiquidoAguaInterior
      );
      const faturamentoLiquidoEsgotoCapital = this.parseCurrencyValue(
        this.form.faturamentoLiquidoEsgotoCapital
      );
      const faturamentoLiquidoEsgotoInterior = this.parseCurrencyValue(
        this.form.faturamentoLiquidoEsgotoInterior
      );

      return {
        cnpj: this.userGessan.cnpj,
        mesAno: this.form.mesAno,
        qtdeUnAbastecimentoAgua:
          quantidadeLigacoesAtivasAguaCapital + quantidadeLigacoesAtivasAguaInterior,
        qtdeUnEsgotamentoSanitario:
          quantidadeLigacoesAtivasEsgotoCapital +
          quantidadeLigacoesAtivasEsgotoInterior,
        faturamentoMensalLiquido:
          faturamentoLiquidoAguaCapital +
          faturamentoLiquidoAguaInterior +
          faturamentoLiquidoEsgotoCapital +
          faturamentoLiquidoEsgotoInterior,
        quantidadeLigacoesAtivasAguaCapital,
        quantidadeLigacoesAtivasAguaInterior,
        quantidadeLigacoesAtivasEsgotoCapital,
        quantidadeLigacoesAtivasEsgotoInterior,
        faturamentoLiquidoAguaCapital,
        faturamentoLiquidoAguaInterior,
        faturamentoLiquidoEsgotoCapital,
        faturamentoLiquidoEsgotoInterior,
      };
    },
    salvarInformeRegulacao() {
      this.isLoading = true;
      this.openDialogConfirmarInforme = false;
      const formData = new FormData();

      formData.append(
        "payload",
        new Blob([JSON.stringify(this.buildPayload())], { type: "application/json" }),
        "payload.json"
      );

      this.activeFields.forEach(field => {
        formData.append(field.documentKey, this.form[field.documentKey]);
      });

      const [mes, ano] = this.form.mesAno.split("/");
      const competencia = `${ano}-${mes.padStart(2, "0")}-01`;
      const formCentral = {
        cnpj: this.userGessan.cnpj,
        nomeResponsavel: this.userGessan.nomeResponsavel,
        cpfResponsavel: this.userGessan.cpfResponsavel,
        aceite: true,
        competencia,
      };

      SaneamentoService.cadastro(formData, this.isGrupoMunicipio).subscribe(
        response => {
          SaneamentoService.historicoCentral(formCentral).subscribe({
            next: () => {},
            error: () => {},
          });

          AuthService.globalStore("success", response["message"]);
          this.$refs.recaptcha.reset();
          this.form.recaptcha = null;
          this.$refs.form.reset();
          this.cleanForm();
          this.isLoading = false;
          router.push('listagem-remuneracoes-regulatorias')
        },
        error => {
          this.isLoading = false;
        }
      );
    },
    onSubmit() {
      if (this.isFormDisabled || !this.activeFields.length) {
        return;
      }

      if (this.isGrupoMunicipio && !this.isCompetenciaPermitida(this.form.mesAno)) {
        this.mensagemErro = this.getCompetenciaValidationMessage();
        this.openDialogErro = true;
        return;
      }

      const isValid = this.$refs.form.validate();
      const validateDocumentos = this.validateDocumentos();

      if (!validateDocumentos) {
        AuthService.globalStore("error", "Anexe todos os documentos obrigatórios em PDF.");
      }

      if (isValid && validateDocumentos) {
        this.openDialogConfirmarInforme = true;
      }
    },
    formatFields() {
      this.activeFields.forEach(field => {
        const value = this.form[field.model];

        if (!value) {
          return;
        }

        this.form[field.model] =
          field.type === "currency"
            ? FunctionsCommuns.MonetaryFormat(value)
            : FunctionsCommuns.IntegerFormatTextField(value);
      });
    },
    checkIfCanCreateInforme() {
      const canCreateByDay = this.validacaoMesAno();

      this.mensagemErro = defaultCreateInformeErrorMessageMunicipio;

      SaneamentoService.podeCriarInformeService(this.userGessan.cnpj).subscribe({
        next: (response: any) => {
          const canCreate =
            this.normalizePodeCriarInformeResponse(response) || canCreateByDay;
          this.openDialogErro = !canCreate;
          this.isCreateDisabled = !canCreate;
        },
        error: () => {
          this.openDialogErro = !canCreateByDay;
          this.isCreateDisabled = !canCreateByDay;
        },
      });
    },
    cleanForm() {
      this.setForm({
        mesAno: null,
        qtdeUnAbastecimentoAgua: "",
        qtdeUnAbastecimentoAguaDocumento: null,
        qtdeUnEsgotamentoSanitario: "",
        qtdeUnEsgotamentoSanitarioDocumento: null,
        faturamentoMensalLiquido: "",
        faturamentoMensalLiquidoDocumento: null,
        quantidadeLigacoesAtivasAguaCapital: "",
        quantidadeLigacoesAtivasAguaCapitalDocumento: null,
        quantidadeLigacoesAtivasAguaInterior: "",
        quantidadeLigacoesAtivasAguaInteriorDocumento: null,
        quantidadeLigacoesAtivasEsgotoCapital: "",
        quantidadeLigacoesAtivasEsgotoCapitalDocumento: null,
        quantidadeLigacoesAtivasEsgotoInterior: "",
        quantidadeLigacoesAtivasEsgotoInteriorDocumento: null,
        faturamentoLiquidoAguaCapital: "",
        faturamentoLiquidoAguaCapitalDocumento: null,
        faturamentoLiquidoAguaInterior: "",
        faturamentoLiquidoAguaInteriorDocumento: null,
        faturamentoLiquidoEsgotoCapital: "",
        faturamentoLiquidoEsgotoCapitalDocumento: null,
        faturamentoLiquidoEsgotoInterior: "",
        faturamentoLiquidoEsgotoInteriorDocumento: null,
        recaptcha: null,
      });
      this.filesErros = [];
      this.filesTypeErros = [];
      if (this.isGrupoMunicipio) {
        this.applyCompetenciaDisponivel();
        return;
      }

      this.mes();
      this.checkIfCanCreateInforme();
    },
  },
};
